/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";
import { AppVisibilityProvider } from "../infrastructure/AppVisibilityProvider";
import type { IAppVisibilityProvider } from "../interfaces/IAppVisibilityProvider";
import type { INetworkAvailabilityService } from "../interfaces/INetworkAvailabilityService";
import type { INetworkResourceListener } from "../interfaces/INetworkResourceListener";
import { NetworkAvailabilityServiceImpl } from "../network/NetworkAvailabilityServiceImpl";
import { NetworkResource } from "../enums/NetworkResource";
import type { INetworkResourceAvailability } from "../types";
import { ConnectionTrigger } from "./types";

/**
 * Callback invoked when a lifecycle event requires a WebSocket action.
 *
 * @param trigger - The connection trigger that caused the event
 */
type LifecycleCallback = (trigger: ConnectionTrigger) => void;

/**
 * Web platform lifecycle manager for WebSocket reconnection triggers.
 *
 * **REQ-WEB-LIFE-02:** Web-specific connection triggers (page load, visibility, network).
 * **REQ-WEB-LIFE-03:** Reconnection on visibility restore.
 * **REQ-WEB-LIFE-04:** Reconnection on network restore.
 * **REQ-WEB-LIFE-05:** Suspend retry timers when page hidden.
 * **REQ-WEB-MON-06:** Page visibility monitoring (via AppVisibilityProvider).
 * **REQ-WEB-MON-07:** Network event monitoring (via NetworkAvailabilityService).
 *
 * Visibility changes are received from the centralized AppVisibilityProvider
 * singleton, which listens to the browser `document.visibilitychange` event.
 * Network availability is monitored via the NetworkAvailabilityService, which
 * evaluates the full network dependency graph (interface → internet) and
 * delivers settled availability changes through INetworkResourceListener.
 */
export class WebSocketLifecycleManager {
  private static readonly TAG: string = "WebSocketLifecycleManager";

  private mOnConnect: LifecycleCallback | null = null;
  private mOnSuspend: (() => void) | null = null;
  private mOnResume: (() => void) | null = null;

  private mVisibilityProvider: IAppVisibilityProvider | null = null;
  private mBoundVisibilityCallback: ((isBackground: boolean) => void) | null =
    null;
  private mNetworkService: INetworkAvailabilityService | null = null;
  private mNetworkListener: INetworkResourceListener | null = null;
  private mBoundUnloadHandler: (() => void) | null = null;

  private mIsPageVisible: boolean = true;
  private mIsNetworkOnline: boolean = true;
  private mIsStarted: boolean = false;

  /**
   * REQ-WEB-LIFE-03 / REQ-WEB-LIFE-05:
   * Visibility callback from AppVisibilityProvider.
   *
   * @param isBackground - true when the app moved to background, false when foreground
   *
   * - Background → suspend retry timers. Active connection MUST NOT be closed.
   * - Foreground → resume timers. If no active connection, trigger reconnect.
   */
  private handleVisibilityCallback(isBackground: boolean): void {
    const isVisible: boolean = !isBackground;

    if (isVisible === this.mIsPageVisible) {
      return; // No actual change
    }

    this.mIsPageVisible = isVisible;

    if (isVisible) {
      Log.d(
        WebSocketLifecycleManager.TAG,
        "handleVisibilityCallback(): Page became visible — resuming"
      );

      if (this.mOnResume !== null) {
        this.mOnResume();
      }

      // REQ-WEB-LIFE-03: Trigger reconnect if needed
      if (this.mOnConnect !== null) {
        this.mOnConnect(ConnectionTrigger.APP_FOREGROUND);
      }
    } else {
      Log.d(
        WebSocketLifecycleManager.TAG,
        "handleVisibilityCallback(): Page became hidden — suspending timers"
      );

      if (this.mOnSuspend !== null) {
        this.mOnSuspend();
      }
    }
  }

  /**
   * REQ-WEB-LIFE-04 / REQ-WEB-MON-07:
   * Network availability change handler via NetworkAvailabilityService.
   *
   * Called when the INTERNET resource availability changes in the dependency graph.
   * - Available → resume retry timers and trigger reconnect if no active connection.
   * - Unavailable → suspend retry timers.
   *
   * @param availability - The settled availability state for INTERNET
   */
  private handleNetworkAvailabilityChanged(
    availability: INetworkResourceAvailability
  ): void {
    const wasOnline: boolean = this.mIsNetworkOnline;
    this.mIsNetworkOnline = availability.isAvailable;

    if (this.mIsNetworkOnline === wasOnline) {
      return; // No actual change
    }

    if (this.mIsNetworkOnline) {
      Log.d(
        WebSocketLifecycleManager.TAG,
        "handleNetworkAvailabilityChanged(): Internet became available"
      );

      if (this.mOnResume !== null) {
        this.mOnResume();
      }

      if (this.mOnConnect !== null) {
        this.mOnConnect(ConnectionTrigger.NETWORK_RESTORED);
      }
    } else {
      Log.d(
        WebSocketLifecycleManager.TAG,
        `handleNetworkAvailabilityChanged(): Internet became unavailable (reason=${availability.failureReason})`
      );

      if (this.mOnSuspend !== null) {
        this.mOnSuspend();
      }
    }
  }

  /**
   * CON-WEB-07:
   * Page unload handler — cleanup all resources.
   *
   * Placeholder: In the future, this will trigger a full
   * WebSocket manager shutdown via the orchestrator.
   */
  private handleUnload(): void {
    Log.d(
      WebSocketLifecycleManager.TAG,
      "handleUnload(): Page unloading — requesting cleanup"
    );
    // The WebSocketManager.destroy() will be called externally.
    // This handler ensures the lifecycle manager itself is cleaned up.
    this.stop();
  }

  /**
   * Start listening for web platform lifecycle events.
   *
   * @param onConnect - Called when a reconnection should be attempted
   * @param onSuspend - Called when retry timers should be suspended (page hidden/offline)
   * @param onResume - Called when retry timers should be resumed (page visible/online)
   */
  public start(
    onConnect: LifecycleCallback,
    onSuspend: () => void,
    onResume: () => void
  ): void {
    if (this.mIsStarted) {
      Log.w(
        WebSocketLifecycleManager.TAG,
        "start(): Already started — ignoring duplicate call"
      );
      return;
    }

    this.mOnConnect = onConnect;
    this.mOnSuspend = onSuspend;
    this.mOnResume = onResume;
    this.mIsStarted = true;

    // REQ-WEB-MON-06: Page Visibility monitoring via AppVisibilityProvider
    this.mVisibilityProvider = AppVisibilityProvider.getInstance();
    this.mIsPageVisible = !this.mVisibilityProvider.isInBackground();
    this.mBoundVisibilityCallback = this.handleVisibilityCallback.bind(this);
    this.mVisibilityProvider.subscribe(this.mBoundVisibilityCallback);

    // REQ-WEB-MON-07: Network event monitoring via NetworkAvailabilityService
    this.mNetworkService = NetworkAvailabilityServiceImpl.getInstance();
    const internetAvailability: INetworkResourceAvailability =
      this.mNetworkService.getAvailability(NetworkResource.INTERNET);
    this.mIsNetworkOnline = internetAvailability.isAvailable;

    this.mNetworkListener = {
      onNetworkResourceAvailabilityChanged: (
        _resource: NetworkResource,
        availability: INetworkResourceAvailability
      ): void => {
        this.handleNetworkAvailabilityChanged(availability);
      }
    };
    this.mNetworkService.addListener(
      NetworkResource.INTERNET,
      this.mNetworkListener
    );

    // CON-WEB-07: Cleanup on page unload
    this.mBoundUnloadHandler = this.handleUnload.bind(this);
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", this.mBoundUnloadHandler);
      window.addEventListener("beforeunload", this.mBoundUnloadHandler);
    }

    Log.d(
      WebSocketLifecycleManager.TAG,
      `start(): Lifecycle listeners registered (visible=${this.mIsPageVisible}, online=${this.mIsNetworkOnline})`
    );
  }

  /**
   * Stop listening for lifecycle events and remove all listeners.
   */
  public stop(): void {
    if (!this.mIsStarted) {
      return;
    }

    // Unsubscribe from AppVisibilityProvider
    if (
      this.mVisibilityProvider !== null &&
      this.mBoundVisibilityCallback !== null
    ) {
      this.mVisibilityProvider.unsubscribe(this.mBoundVisibilityCallback);
    }

    // Unsubscribe from NetworkAvailabilityService
    if (this.mNetworkService !== null && this.mNetworkListener !== null) {
      this.mNetworkService.removeListener(
        NetworkResource.INTERNET,
        this.mNetworkListener
      );
    }

    if (typeof window !== "undefined") {
      if (this.mBoundUnloadHandler !== null) {
        window.removeEventListener("pagehide", this.mBoundUnloadHandler);
        window.removeEventListener("beforeunload", this.mBoundUnloadHandler);
      }
    }

    this.mVisibilityProvider = null;
    this.mBoundVisibilityCallback = null;
    this.mNetworkService = null;
    this.mNetworkListener = null;
    this.mBoundUnloadHandler = null;
    this.mOnConnect = null;
    this.mOnSuspend = null;
    this.mOnResume = null;
    this.mIsStarted = false;

    Log.d(WebSocketLifecycleManager.TAG, "stop(): Lifecycle listeners removed");
  }

  /**
   * Whether the page is currently visible.
   */
  public isPageVisible(): boolean {
    return this.mIsPageVisible;
  }

  /**
   * Whether the network is currently online.
   */
  public isNetworkOnline(): boolean {
    return this.mIsNetworkOnline;
  }
}
